// Owner and manager accounts: roles per venue, the permission table from the spec, phone numbers as sign-in
// identity, and the one-time code rules (6 digits, 10 minutes, 5 wrong codes lock the phone for 15 minutes).
// Pure functions, no I/O. Staff never have an account: they use the venue PIN (rules in staff.ts).
import { PIN_LOCKOUT } from './staff';

// ---- roles and permissions ----

/** Roles a signed-in user holds in a membership. `staff` is the PIN session on a counter device, never a user. */
export type MemberRole = 'owner' | 'manager';
export type Role = MemberRole | 'staff';

const ROLE_RANK: Record<Role, number> = { staff: 1, manager: 2, owner: 3 };

export type Action =
  | 'create_venue'
  | 'invite_user'
  | 'see_all_venues'
  | 'edit_settings'
  | 'see_dashboard'
  | 'see_feedback'
  | 'reply_note'
  | 'use_staff_screen'
  | 'open_board';

/** Minimum role per action, from the spec's "Permissions by role" table. */
export const PERMISSIONS: Record<Action, Role> = {
  create_venue: 'owner',
  invite_user: 'owner',
  see_all_venues: 'owner',
  edit_settings: 'manager',
  see_dashboard: 'manager',
  see_feedback: 'manager',
  reply_note: 'manager',
  use_staff_screen: 'staff',
  open_board: 'staff',
};

export function roleAllows(role: Role | null, min: Role): boolean {
  return role !== null && ROLE_RANK[role] >= ROLE_RANK[min];
}

export function can(role: Role | null, action: Action): boolean {
  return roleAllows(role, PERMISSIONS[action]);
}

/** A row of the memberships table: a role on one venue, or on every venue of the organization when `venueId` is null. */
export interface Membership {
  orgId: string;
  venueId: string | null;
  role: MemberRole;
}

/** The highest role this user holds at a venue: an organization-wide row or a row naming the venue; null when none. */
export function roleAtVenue(memberships: readonly Membership[], venue: { id: string; orgId: string }): MemberRole | null {
  let best: MemberRole | null = null;
  for (const m of memberships) {
    if (m.orgId !== venue.orgId) continue;
    if (m.venueId !== null && m.venueId !== venue.id) continue;
    if (best === null || ROLE_RANK[m.role] > ROLE_RANK[best]) best = m.role;
  }
  return best;
}

/** Owners see every venue of their organization; a manager sees only the venues named in their rows. */
export function isOrgOwner(memberships: readonly Membership[], orgId: string): boolean {
  return memberships.some((m) => m.orgId === orgId && m.venueId === null && m.role === 'owner');
}

/**
 * The role a user holds at the organization level: owner with an organization-wide owner row, manager when any
 * row names a venue of it (a manager may not create venues or invite), null otherwise.
 */
export function roleAtOrg(memberships: readonly Membership[], orgId: string): MemberRole | null {
  if (isOrgOwner(memberships, orgId)) return 'owner';
  return memberships.some((m) => m.orgId === orgId) ? 'manager' : null;
}

export function isMemberRole(s: unknown): s is MemberRole {
  return s === 'owner' || s === 'manager';
}

// ---- phone numbers ----

const MOROCCO = '212';

/**
 * A phone number typed by an owner, as E.164 digits without the plus (the form every provider in this code base uses).
 * Accepts spaces, dots, dashes and parentheses, a leading + or 00, Arabic-Indic digits, and Moroccan local numbers
 * (06 12 34 56 78 becomes 212612345678). Returns null when the result is not a plausible number.
 */
export function normalizePhone(input: string, defaultCountry: string = MOROCCO): string | null {
  let s = input.trim();
  if (s.startsWith('+')) s = `00${s.slice(1)}`;
  s = s
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[\s().-]/g, '');
  if (!/^\d+$/.test(s)) return null;
  if (s.startsWith('00')) s = s.slice(2);
  else if (s.startsWith('0')) s = defaultCountry + s.slice(1);
  else if (s.length <= 9) s = defaultCountry + s;
  if (s.startsWith('0')) return null;
  if (s.startsWith(MOROCCO) && s.length !== 12) return null;
  if (s.length < 8 || s.length > 15) return null;
  return s;
}

/** Display form, always LTR: +212 6 12 34 56 78 for Morocco, +<digits> elsewhere. */
export function formatPhone(digits: string): string {
  if (digits.startsWith(MOROCCO) && digits.length === 12) {
    const n = digits.slice(3);
    return `+212 ${n[0]} ${n.slice(1, 3)} ${n.slice(3, 5)} ${n.slice(5, 7)} ${n.slice(7, 9)}`;
  }
  return `+${digits}`;
}

/** What a log line may show: the last four digits only. */
export function phoneForLog(digits: string): string {
  return `…${digits.slice(-4)}`;
}

// ---- e-mail addresses (the backup channel for the sign-in code, issue #23) ----

/**
 * An address typed on the account page, as stored: trimmed and lower-cased, one @, no spaces, a dot in the domain,
 * at most 254 characters. Null when it is not a plausible address; the provider does the rest.
 */
export function normalizeEmail(input: string): string | null {
  const s = input.trim().toLowerCase();
  if (s.length < 6 || s.length > 254) return null;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s) ? s : null;
}

/**
 * What the public sign-in page and the logs may show of a backup address: the first character of the local part
 * and of the host, then the top-level domain (yasmine@azulcomputing.com → y•••@a•••.com).
 */
export function maskEmail(email: string): string {
  const at = email.lastIndexOf('@');
  if (at < 1) return '•••';
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  const dot = domain.lastIndexOf('.');
  const host = dot > 0 ? domain.slice(0, dot) : domain;
  const tld = dot > 0 ? domain.slice(dot) : '';
  return `${local[0]}•••@${host[0] ?? ''}•••${tld}`;
}

// ---- one-time codes ----

export const LOGIN_CODE = {
  length: 6,
  /** A code is valid for 10 minutes after it was sent. */
  ttlMs: 10 * 60_000,
  /** Minimum gap between two codes to the same phone. */
  resendCooldownMs: 60_000,
} as const;

/** Wrong codes count like wrong PINs: five inside 15 minutes lock the phone (and the IP) for 15 minutes. */
export const LOGIN_LOCKOUT = PIN_LOCKOUT;

/** A signed-in owner or manager stays signed in for 30 days unless the session is revoked. */
export const USER_SESSION_TTL_MS = 30 * 24 * 60 * 60_000;

export type LoginChannel = 'whatsapp' | 'email';

/** Six random digits, uniform (values above the last full multiple of 10^6 are rejected), leading zeros kept. */
export function generateLoginCode(): string {
  const buf = new Uint32Array(1);
  const limit = 4_294_000_000; // 4294 * 10^6
  let n: number;
  do {
    crypto.getRandomValues(buf);
    n = buf[0];
  } while (n >= limit);
  return String(n % 1_000_000).padStart(LOGIN_CODE.length, '0');
}

/** The code as typed: digits only, spaces allowed, Arabic-Indic digits accepted. Null unless exactly six digits. */
export function parseLoginCode(input: string): string | null {
  const s = input
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/\s/g, '');
  return /^\d{6}$/.test(s) ? s : null;
}

export function loginCodeExpiresAt(sentAt: number): number {
  return sentAt + LOGIN_CODE.ttlMs;
}

export function isLoginCodeExpired(code: { expiresAt: number; consumedAt: number | null }, now: number): boolean {
  return code.consumedAt !== null || now >= code.expiresAt;
}

/** Milliseconds before another code may be sent to this phone; 0 when one can go out now. */
export function resendWaitMs(lastSentAt: number | null, now: number): number {
  if (lastSentAt === null) return 0;
  return Math.max(0, lastSentAt + LOGIN_CODE.resendCooldownMs - now);
}
